import { useEffect } from 'react';
import { useThirdPartyOAuth } from '@modules/settings/integrations/hooks/use-third-party-oauth';
import { TRANSIENT_TOKEN, ZOHO_THIRD_PARTY_SLUG } from '@modules/settings/integrations/constants/integration.constants';
import Cookies from 'js-cookie';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkspaceStatus } from '@shared/hooks/use-workspace-status';

const INTEGRATIONS_PATH = '/settings/integrations';

export const useHandleOAuthSideEffects = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSignUpCompleted } = useWorkspaceStatus();

  /**
   * Needs to revisit PipeDrive OAuth with FederatedSignUp feature
   */
  const isFederatedSignUpPage = location?.pathname == '/auth/callback';
  const [searchParams] = useSearchParams();
  const { handleExchangeViaTransientToken, handleCacheOAuth, handleExchangeOauthForToken } = useThirdPartyOAuth();
  const transientToken = Cookies.get(TRANSIENT_TOKEN);

  const { t } = useTranslation();

  const thirdPartyOAuthCode = searchParams.get('code');
  const thirdPartyOAuthError = searchParams.get('error');
  const thirdPartyName = searchParams.get('thirdPartyName');
  const isZohoCallback = thirdPartyName?.toLowerCase() === ZOHO_THIRD_PARTY_SLUG;

  // Providers redirect back to `/app/settings/integrations`, which isn't a route in this app.
  // A `code` redirect is handled right here on any path, but an `error` redirect is only handled
  // by the Integrations page (`use-integrations.ts`), so move it there with its query params.
  useEffect(() => {
    if (thirdPartyOAuthError && thirdPartyName && location.pathname !== INTEGRATIONS_PATH) {
      navigate(`${INTEGRATIONS_PATH}${location.search}`, { replace: true });
    }
  }, [thirdPartyOAuthError, thirdPartyName]);

  // Cache OAuth code if not logged in
  useEffect(() => {
    if (!isSignUpCompleted && thirdPartyOAuthCode && !isFederatedSignUpPage) {
      // The logged-out cache path cannot carry Zoho's `accounts-server` (the backend rejects it),
      // so ask the user to log in and reconnect instead. Drop the one-time code from the URL so it is
      // never exchanged later, after login, and a refresh does not repeat the toast.
      if (isZohoCallback) {
        notification.info({ key: 'zoho-logged-out-reconnect', message: t('please_login_and_reconnect_zoho') });
        navigate(location.pathname, { replace: true });
        return;
      }
      handleCacheOAuth();
      handleExchangeOauthForToken();
    }
  }, [isSignUpCompleted, thirdPartyOAuthCode]);

  useEffect(() => {
    if (isSignUpCompleted && thirdPartyOAuthCode) {
      handleExchangeOauthForToken();
    }
  }, [isSignUpCompleted, thirdPartyOAuthCode]);

  // Exchange via transient token if logged in
  useEffect(() => {
    if (isSignUpCompleted && transientToken && !isFederatedSignUpPage) {
      handleExchangeViaTransientToken();
    }
  }, [isSignUpCompleted, transientToken]);
};
